/**
 * BlumeAdmin UI Service
 *
 * This service provides a convenient API for interacting with the BlumeAdmin UI state.
 * It wraps NGXS store operations for side menu expansion and other blume-admin UI settings.
 *
 * Benefits:
 * - Type-safe API for BlumeAdmin UI state operations
 * - Simplified action dispatching
 * - Easy state selection with observables
 * - Encapsulates NGXS implementation details
 *
 * @example
 * constructor(private blumeAdminUIService: BlumeAdminUIService) {
 *   this.blumeAdminUIService.sideMenuExpanded$.subscribe(expanded => {
 *     console.log('Side menu expanded:', expanded);
 *   });
 * }
 */

import { Injectable } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import { BlumeAdminUIState, BlumeAdminUIStateModel } from './blume-admin-ui.state';
import {
  InitializeBlumeAdminUIStateAction,
  SetBlumeAdminSideMenuExpandedAction,
  ToggleBlumeAdminSideMenuExpandedAction,
} from './blume-admin-ui.actions';

/**
 * BlumeAdmin UI Service
 *
 * Provides methods for managing BlumeAdmin UI state including side menu expansion
 * and other blume-admin-specific UI preferences.
 */
@Injectable({
  providedIn: 'root',
})
export class BlumeAdminUIService {
  // ========================================
  // Observable Selectors
  // ========================================

  /**
   * Observable of the entire BlumeAdmin UI state
   *
   * @example
   * this.blumeAdminUIService.blumeAdminUIState$.subscribe(state => {
   *   console.log('BlumeAdmin UI State:', state);
   * });
   */
  @Select(BlumeAdminUIState.getState)
  blumeAdminUIState$!: Observable<BlumeAdminUIStateModel>;

  /**
   * Observable of the side menu expanded state
   *
   * @example
   * this.blumeAdminUIService.sideMenuExpanded$.subscribe(expanded => {
   *   console.log('Side menu expanded:', expanded);
   * });
   */
  @Select(BlumeAdminUIState.sideMenuExpanded)
  sideMenuExpanded$!: Observable<boolean>;

  // ========================================
  // Constructor
  // ========================================

  constructor(private store: Store) {}

  // ========================================
  // Initialization
  // ========================================

  /**
   * Initialize the BlumeAdmin UI state
   *
   * Loads saved preferences from localStorage.
   * Should be called once when the blume-admin route is accessed.
   *
   * @returns A promise that resolves when initialization is complete
   *
   * @example
   * // In blume-admin.component.ts
   * ngOnInit() {
   *   this.blumeAdminUIService.initialize();
   * }
   */
  initialize(): Promise<void> {
    return this.store.dispatch(new InitializeBlumeAdminUIStateAction()).toPromise();
  }

  // ========================================
  // Side Menu Management
  // ========================================

  /**
   * Get the current side menu expanded state (synchronous)
   *
   * @returns Whether the side menu is currently expanded
   *
   * @example
   * const isExpanded = this.blumeAdminUIService.getSideMenuExpanded();
   * console.log('Side menu expanded:', isExpanded);
   */
  getSideMenuExpanded(): boolean {
    return this.store.selectSnapshot(BlumeAdminUIState.sideMenuExpanded);
  }

  /**
   * Set the side menu expanded state
   *
   * @param expanded - Whether the side menu should be expanded
   * @returns A promise that resolves when the action is complete
   *
   * @example
   * // Expand side menu
   * this.blumeAdminUIService.setSideMenuExpanded(true);
   *
   * @example
   * // Collapse side menu
   * this.blumeAdminUIService.setSideMenuExpanded(false);
   */
  setSideMenuExpanded(expanded: boolean): Promise<void> {
    return this.store.dispatch(new SetBlumeAdminSideMenuExpandedAction(expanded)).toPromise();
  }

  /**
   * Expand the side menu
   *
   * Convenience method that sets side menu expanded to true.
   *
   * @returns A promise that resolves when the action is complete
   *
   * @example
   * this.blumeAdminUIService.expandSideMenu();
   */
  expandSideMenu(): Promise<void> {
    return this.setSideMenuExpanded(true);
  }

  /**
   * Collapse the side menu
   *
   * Convenience method that sets side menu expanded to false.
   *
   * @returns A promise that resolves when the action is complete
   *
   * @example
   * this.blumeAdminUIService.collapseSideMenu();
   */
  collapseSideMenu(): Promise<void> {
    return this.setSideMenuExpanded(false);
  }

  /**
   * Toggle the side menu expanded state
   *
   * If the side menu is currently expanded, it will be collapsed, and vice versa.
   *
   * @returns A promise that resolves when the action is complete
   *
   * @example
   * this.blumeAdminUIService.toggleSideMenu();
   */
  toggleSideMenu(): Promise<void> {
    return this.store.dispatch(new ToggleBlumeAdminSideMenuExpandedAction()).toPromise();
  }

}

